import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { useConfig } from 'config/useConfig';
import { getDisplayRuntimeProfile } from 'config/displayRuntime';
const proximalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-proximal').trim();
const midColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-mid').trim();
const distalColor = getComputedStyle(document.documentElement).getPropertyValue('--text-section-distal').trim();

export interface SelectedIndices {
  proximal: number;
  mid: number;
  distal: number;
}

interface PlaqueChartProps {
  lengthArr: number[];
  lumenArr: number[] | Float32Array;
  wallArr: number[] | Float32Array;
  lowArr: number[];
  noncalcArr: number[];
  calcArr: number[];
  width: number;
  height: number;
  windowSize?: number;
  yLabel?: string;
  title?: string;

  dragNodeId: number;
  selectedIndices?: SelectedIndices;

  draggingKey: 'proximal' | 'mid' | 'distal' | null;

  onMarkerMouseDown?: (key: 'proximal' | 'mid' | 'distal' | null) => void;
  onDragStart?: (e: PointerEvent, index: number) => void;
  onDrag?: (e: PointerEvent, index: number) => void;
  onDragEnd?: () => void;
}

function movingAverage(arr: ArrayLike<number>, window: number) {
  if (window <= 1) return Array.from(arr);
  const out = [];
  const n = arr.length;
  const half = Math.floor(window / 2);
  for (let i = 0; i < n; ++i) {
    let sum = 0,
      cnt = 0;
    for (let j = i - half; j <= i + half; ++j)
      if (j >= 0 && j < n) {
        sum += arr[j];
        cnt++;
      }
    out.push(sum / cnt);
  }
  return out;
}

const PlaqueChart: React.FC<PlaqueChartProps> = ({
  lengthArr,
  lumenArr,
  wallArr,
  lowArr,
  noncalcArr,
  calcArr,
  width,
  height,
  windowSize = 10,
  yLabel = 'Area (mm²)',
  title = 'Plaque Area Graph',
  dragNodeId,
  selectedIndices,
  draggingKey,
  onMarkerMouseDown,
  onDragStart,
  onDrag,
  onDragEnd,
}) => {
  if (!lengthArr || !lumenArr || !wallArr || !lowArr || !noncalcArr || !calcArr)
    return null;

  const svgRef = useRef<SVGSVGElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const { config } = useConfig();
  const [, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const lastDragIndexRef = useRef<number | null>(null);
  const callbacksRef = useRef({
    onMarkerMouseDown,
    onDragStart,
    onDrag,
    onDragEnd,
  });

  useEffect(() => {
    callbacksRef.current = {
      onMarkerMouseDown,
      onDragStart,
      onDrag,
      onDragEnd,
    };
  });
  const fontScale = useMemo(() => {
    if (!width || !height) return 1;
    const wScale = width / 720;
    const hScale = height / 280;
    const typographyScale = getDisplayRuntimeProfile().typographyScale;
    return Math.max(0.7, Math.min(1.22, Math.min(wScale, hScale) * typographyScale));
  }, [width, height]);
  const axisFontSize = Math.max(9, Math.round(12 * fontScale));
  const titleFontSize = Math.max(11, Math.round(13 * fontScale));
  const labelFontSize = Math.max(10, Math.round(12 * fontScale));
  const legendFontSize = Math.max(10, Math.round(11 * fontScale));

  const clamp = (v: number, min: number, max: number) =>
    Math.max(min, Math.min(max, v));
  function drawLabel(
    g: any,
    xPx: number,
    yPx: number,
    color: string,
    text: string,
    prefer: 'start' | 'middle' | 'end' = 'end',
  ) {
    const M = 8; // 캔버스 안쪽 여백
    let x = clamp(xPx, M, innerWidth - M);

    // 가장자리에서는 앵커 자동 전환
    let anchor: 'start' | 'middle' | 'end' = prefer;
    if (x <= M + 10) anchor = 'start';
    else anchor = 'end';
    //else if (x >= innerWidth - (M + 32)) anchor = 'end';

    const sel = g
      .append('text')
      .attr('class', 'one-line')
      .attr('x', x)
      .attr('y', yPx)
      .attr('text-anchor', anchor)
      .attr('dominant-baseline', 'middle')
      .attr('fill', color)
      .attr('font-size', `${Math.max(10, Math.round(14 * fontScale))}px`)
      .attr('font-weight', 'bold')
      // .attr('paint-order', 'stroke')
      // .attr('stroke', '#0D0D0D')
      // .attr('stroke-width', 3)
      // .style('pointer-events', 'none')
      .text(text);

    // 실제 bbox 기준 보정 (폰트/문자폭 변화 대비)
    const bbox = (sel.node() as SVGTextElement).getBBox();
    if (bbox.x < 0) {
      sel.attr('text-anchor', 'start').attr('x', M);
    } else if (bbox.x + bbox.width > innerWidth) {
      sel.attr('text-anchor', 'end').attr('x', innerWidth - M);
    }
  }

  useEffect(() => {
    const n = Math.min(
      lengthArr.length,
      lumenArr.length,
      wallArr.length,
      lowArr.length,
      noncalcArr.length,
      calcArr.length,
    );
    if (n < 2) return;

    const margin = {
      top: Math.max(14, Math.round(labelFontSize * 1.6)),
      right: Math.max(30, Math.round(axisFontSize * 3.5)),
      bottom: Math.max(
        40,
        Math.round((axisFontSize + labelFontSize) * 2.2),
      ),
      left: Math.max(46, Math.round(labelFontSize * 3.8)),
    };
    const innerWidth = Math.max(0, width - margin.left - margin.right);
    const innerHeight = Math.max(0, height - margin.top - margin.bottom);

    const xs = lengthArr.slice(0, n);
    const lumen = movingAverage(lumenArr.slice(0, n), windowSize);
    const wall = movingAverage(wallArr.slice(0, n), windowSize);
    const low = movingAverage(lowArr.slice(0, n), windowSize);
    const nonc = movingAverage(noncalcArr.slice(0, n), windowSize);
    const calc = movingAverage(calcArr.slice(0, n), windowSize);

    const orilumen = lumenArr.slice(0, n);
    const orilow = lowArr.slice(0, n);
    const orinonc = noncalcArr.slice(0, n);
    const oricalc = calcArr.slice(0, n);

    const minY = 0;
    const maxY = d3.max([...lumen, ...wall]) ?? 1;

    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(xs) as [number, number])
      .range([0, innerWidth]);
    const yScale = d3
      .scaleLinear()
      .domain([minY, maxY * 1.05])
      .range([innerHeight, 0]);

    const bottomSafe = Math.max(4, Math.round(labelFontSize * 0.4));

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr(
        'style',
        'max-width:100%; height:auto; height:intrinsic; background-color:#0D0D0D;',
      );
    svg.selectAll('*').remove();

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);
    gRef.current = g.node();

    const areaBackground = d3
      .area<number>()
      .x((_, i) => xScale(xs[i]))
      .y0(yScale(0))
      .y1((d) => yScale(d));
    g.append('path')
      .datum(lumen)
      .attr('fill', config.colors.LUMEN)
      .attr('opacity', 0.7)
      .attr('d', areaBackground);

    const plaqueStack = xs.map((_, i) => {
      const plaqueSum = (low[i] ?? 0) + (nonc[i] ?? 0) + (calc[i] ?? 0);
      let a = 0,
        b = 0,
        c = 0;
      if (plaqueSum > 0 && wall[i] > lumen[i]) {
        a = ((low[i] ?? 0) / plaqueSum) * (wall[i] - lumen[i]);
        b = ((nonc[i] ?? 0) / plaqueSum) * (wall[i] - lumen[i]);
        c = ((calc[i] ?? 0) / plaqueSum) * (wall[i] - lumen[i]);
      }
      return {
        x: xs[i],
        yLumen: lumen[i],
        yWall: wall[i],
        yLow: lumen[i] + a,
        yNoncalc: lumen[i] + a + b,
        yCalc: lumen[i] + a + b + c,
      };
    });

    const areaLow = d3
      .area<(typeof plaqueStack)[0]>()
      .x((d) => xScale(d.x))
      .y0((d) => yScale(d.yLumen))
      .y1((d) => yScale(d.yLow));
    const areaNoncalc = d3
      .area<(typeof plaqueStack)[0]>()
      .x((d) => xScale(d.x))
      .y0((d) => yScale(d.yLow))
      .y1((d) => yScale(d.yNoncalc));
    const areaCalc = d3
      .area<(typeof plaqueStack)[0]>()
      .x((d) => xScale(d.x))
      .y0((d) => yScale(d.yNoncalc))
      .y1((d) => yScale(d.yCalc));

    g.append('path')
      .datum(plaqueStack)
      .attr('fill', config.colors.lowAttenuation)
      .attr('opacity', 0.85)
      .attr('d', areaLow);
    g.append('path')
      .datum(plaqueStack)
      .attr('fill', config.colors.nonCalcified)
      .attr('opacity', 0.85)
      .attr('d', areaNoncalc);
    g.append('path')
      .datum(plaqueStack)
      .attr('fill', config.colors.CALCIUM)
      .attr('opacity', 0.85)
      .attr('d', areaCalc);

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(xScale).ticks(Math.max(2, Math.floor(width / 120))))
      .call((gg) => gg.select('.domain').remove())
      .selectAll('text')
      .attr('fill', 'white')
      .attr('font-size', `${axisFontSize}px`);

    g.append('g')
      .call(d3.axisLeft(yScale).ticks(height / 60))
      .call((gg) =>
        gg
          .select('.domain')
          .remove(),
      )
      .call((gg) =>
        gg.selectAll('text').attr('fill', '#fff').attr('font-size', `${axisFontSize}px`),
      );

    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', innerHeight)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);
    g.append('line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .attr('stroke', 'white')
      .attr('stroke-width', 1);

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', Math.max(12, Math.round(14 * fontScale)))
      .attr('fill', '#FFE066')
      .attr('text-anchor', 'middle')
      .attr('font-size', `${titleFontSize}px`)
      .text(title);
    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', height - bottomSafe)
      .attr('fill', '#fff')
      .attr('dominant-baseline', 'text-after-edge')
      .attr('text-anchor', 'middle')
      .attr('font-size', `${labelFontSize}px`)
      .text('Length (mm)');
    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', Math.max(10, Math.round(18 * fontScale)))
      .attr('fill', '#fff')
      .attr('text-anchor', 'middle')
      .attr('font-size', `${labelFontSize}px`)
      .text(yLabel);

    const legendItems = [
      { label: 'Low attenuation plaque', color: config.colors.lowAttenuation },
      { label: 'Noncalcified plaque', color: config.colors.nonCalcified },
      { label: 'Calcified plaque', color: config.colors.CALCIUM },
      { label: 'Lumen', color: config.colors.LUMEN },
    ];
    const legendWidth = Math.max(140, Math.round(190 * fontScale));
    const legendX = Math.max(margin.left + 8, width - legendWidth - 8);
    const legendTop = Math.max(16, Math.round(20 * fontScale));
    const legendRow = Math.max(16, Math.round(22 * fontScale));
    const legend = svg.append('g').attr('class', 'plaque-legend');
    legendItems.forEach((item, idx) => {
      legend
        .append('rect')
        .attr('x', legendX)
        .attr('y', legendTop + idx * legendRow + 3)
        .attr('width', 12)
        .attr('height', 12)
        .attr('fill', item.color);
      legend
        .append('text')
        .attr('x', legendX + 17)
        .attr('y', legendTop + idx * legendRow + 13)
        .attr('fill', '#fff')
        .attr('font-size', `${legendFontSize}px`)
        .text(item.label);
    });

    const getNearestDataIndex = (targetX: number) => {
      const lastIndex = xs.length - 1;
      if (lastIndex < 0) return -1;
      if (lastIndex === 0) return 0;

      const firstX = xs[0];
      const lastX = xs[lastIndex];
      if (!Number.isFinite(firstX) || !Number.isFinite(lastX)) {
        let nearest = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;
        for (let i = 0; i <= lastIndex; i += 1) {
          const distance = Math.abs(xs[i] - targetX);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearest = i;
          }
        }
        return nearest;
      }

      const ascending = firstX <= lastX;
      if (ascending) {
        if (targetX <= firstX) return 0;
        if (targetX >= lastX) return lastIndex;
      } else {
        if (targetX >= firstX) return 0;
        if (targetX <= lastX) return lastIndex;
      }

      let lo = 0;
      let hi = lastIndex;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const midX = xs[mid];
        if ((ascending && midX < targetX) || (!ascending && midX > targetX)) {
          lo = mid + 1;
        } else {
          hi = mid;
        }
      }

      const prev = Math.max(0, lo - 1);
      return Math.abs(xs[prev] - targetX) <= Math.abs(xs[lo] - targetX)
        ? prev
        : lo;
    };

    // 차트 별 마우스 event 컨트롤
    svg.on('mousedown', (event: any) => {
      event.preventDefault();
      draggingRef.current = true;
      setDragging(true);
      callbacksRef.current.onMarkerMouseDown?.(null);
      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));
      if (idx >= 0) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDragStart?.(event, idx);
      }
    });
    svg.on('mousemove', (event: any) => {
      if (!draggingRef.current) return;
      event.preventDefault();
      const [mouseX] = d3.pointer(event, g.node());
      const idx = getNearestDataIndex(xScale.invert(mouseX));
      if (idx >= 0 && idx !== lastDragIndexRef.current) {
        lastDragIndexRef.current = idx;
        callbacksRef.current.onDrag?.(event, idx);
      }
    });
    svg.on('mouseup', () => {
      draggingRef.current = false;
      lastDragIndexRef.current = null;
      setDragging(false);
      callbacksRef.current.onDragEnd?.();
    });
    // svg.on('mouseleave', () => {
    //   setDragging(false);
    //   onDragEnd?.();
    // });
    return () => {
      svg.on('mousedown', null);
      svg.on('mousemove', null);
      svg.on('mouseup', null);
      // svg.on('mouseleave', null);
    };
  }, [
    lengthArr,
    lumenArr,
    wallArr,
    lowArr,
    noncalcArr,
    calcArr,
    width,
    height,
    windowSize,
    yLabel,
    title,
    config.colors,
    fontScale,
  ]);

  const drawActiveLine = () => {
    if (!gRef.current) return;

    const g = d3.select(gRef.current);
    g.selectAll('.one-line').remove();

    const n = lengthArr.length;
    if (n < 2 || dragNodeId === -1 || dragNodeId >= n) return;

    const margin = {
      top: Math.max(14, Math.round(labelFontSize * 1.6)),
      right: Math.max(30, Math.round(axisFontSize * 3.5)),
      bottom: Math.max(
        40,
        Math.round((axisFontSize + labelFontSize) * 2.2),
      ),
      left: Math.max(46, Math.round(labelFontSize * 3.8)),
    };
    const innerWidth = Math.max(0, width - margin.left - margin.right);
    const innerHeight = Math.max(0, height - margin.top - margin.bottom);
    const xs = lengthArr.slice(0, n);
    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(xs) as [number, number])
      .range([0, innerWidth]);

    const xPos = xScale(xs[dragNodeId]);
    const textY = innerHeight / 5;
    const orilumen = lumenArr.slice(0, n);
    const orilow = lowArr.slice(0, n);
    const orinonc = noncalcArr.slice(0, n);
    const oricalc = calcArr.slice(0, n);

    if (draggingKey === null) {
      g.append('line')
        .attr('class', 'one-line')
        .attr('x1', xPos)
        .attr('x2', xPos)
        .attr('y1', 0)
        .attr('y2', innerHeight)
        .attr('stroke', '#33FF33')
        .attr('stroke-width', 2);
    }

    drawLabel(
      g,
      xPos - 6,
      textY + 0,
      config.colors.lowAttenuation,
      `${Number.isFinite(orilow[dragNodeId]) ? Number(orilow[dragNodeId]).toFixed(2) : ''} mm²`,
      'end',
    );
    drawLabel(
      g,
      xPos - 6,
      textY + 20,
      config.colors.nonCalcified,
      `${Number.isFinite(orinonc[dragNodeId]) ? Number(orinonc[dragNodeId]).toFixed(2) : ''} mm²`,
      'end',
    );
    drawLabel(
      g,
      xPos - 6,
      textY + 40,
      config.colors.CALCIUM,
      `${Number.isFinite(oricalc[dragNodeId]) ? Number(oricalc[dragNodeId]).toFixed(2) : ''} mm²`,
      'end',
    );
    drawLabel(
      g,
      xPos - 6,
      textY + 60,
      config.colors.LUMEN,
      `${Number.isFinite(orilumen[dragNodeId]) ? Number(orilumen[dragNodeId]).toFixed(2) : ''} mm²`,
      'end',
    );
  };

  useEffect(() => {
    if (!selectedIndices || !svgRef.current || !gRef.current) return;

    const n = lengthArr.length;
    if (n < 2) return;

    const margin = {
      top: Math.max(14, Math.round(labelFontSize * 1.6)),
      right: Math.max(30, Math.round(axisFontSize * 3.5)),
      bottom: Math.max(
        40,
        Math.round((axisFontSize + labelFontSize) * 2.2),
      ),
      left: Math.max(46, Math.round(labelFontSize * 3.8)),
    };
    const innerWidth = Math.max(0, width - margin.left - margin.right);
    const innerHeight = Math.max(0, height - margin.top - margin.bottom);

    const xs = lengthArr.slice(0, n);
    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(xs) as [number, number])
      .range([0, innerWidth]);

    const g = d3.select(gRef.current);
    g.selectAll('.marker-group').remove();

    const strokeWidth = 2;
    const markers = [
      { key: 'proximal' as const, idx: selectedIndices.proximal, color: proximalColor,},
      { key: 'mid' as const, idx: selectedIndices.mid, color: midColor},
      { key: 'distal' as const, idx: selectedIndices.distal, color: distalColor },
    ];

    markers.forEach(({ key, idx, color }) => {
      if (idx >= 0 && idx < xs.length) {
        const xPos = xScale(xs[idx]);

        const markerGroup = g.append('g')
        .attr('class','marker-group')
        .attr('data-key',key)

        markerGroup.append('line')
          .attr('class', 'three-line-hit')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth + 10)
          .attr('stroke-opacity', 0)
          .style('cursor', 'move')
          .on('mousedown', (ev: any) => {
            ev.preventDefault();
            ev.stopPropagation();
            draggingRef.current = true;
            lastDragIndexRef.current = idx;
            setDragging(true);
            callbacksRef.current.onMarkerMouseDown?.(key);
            callbacksRef.current.onDragStart?.(ev, idx);
          });

        markerGroup.append('line')
          .attr('class', 'three-line')
          .attr('x1', xPos)
          .attr('x2', xPos)
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', color)
          .attr('stroke-width', strokeWidth)
          .style('pointer-events', 'none');
      }
    });
  }, [
    selectedIndices,
    lengthArr,
    width,
    height,
    fontScale,
  ]);
  useEffect(() => {
    drawActiveLine();
  }, [
    dragNodeId,
    draggingKey,
    lengthArr,
    lumenArr,
    lowArr,
    noncalcArr,
    calcArr,
    width,
    height,
    fontScale,
    config.colors,
  ]);

  return <svg ref={svgRef} style={{ userSelect: 'none' }} />;
};

export default PlaqueChart;
